import React, { useRef, useEffect, useCallback, useState } from 'react';
import _find from 'lodash/find.js';
import _noop from 'lodash/noop.js';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { parallax } from '_ui-core-js/libraries/parallax.js';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { BackgroundMedia } from '_ui-core-js/React/components/BackgroundMedia.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { Picture } from '_ui-core-js/React/components/Picture.jsx';
import { AnimatedTextHeader } from '_ui-core-js/React/components/AnimatedTextHeader.jsx';
import { MEDIA_TYPES } from '_ui-core-js/constants/config.js';
import { VIEWS } from './StyleSelector.jsx';

const parallaxImageProps = [
  {
    key: 'parallax-image-1',
    gridClasses: 'col-4 col-md-3',
    parallaxSpeed: 0.15,
    parallaxContraint: 120,
    top: '10%'
  },
  {
    key: 'parallax-image-2',
    gridClasses: 'col-2 col-md-1 offset-6 offset-md-7 ',
    parallaxSpeed: 0.2,
    parallaxContraint: 120,
    top: 0
  },
  {
    key: 'parallax-image-3',
    gridClasses: 'col-3 col-md-2 offset-8 offset-sm-9',
    parallaxSpeed: 0.3,
    parallaxContraint: 120,
    top: '45%'
  },
  {
    key: 'parallax-image-4',
    gridClasses: 'col-3 col-md-2 offset-9 offset-sm-11',
    parallaxSpeed: 0.1,
    parallaxContraint: 120,
    top: '15%'
  }
];

export function LandingScreen(props) {

  const { block, state, rootElRef, data, user, onClickActions } = props;
  const { isUserLoggedIn } = user;
  const curosrParallaxRef = useRef();
  const hasParallaxImages = !!data.images?.length;
  const [ctas, setCtas ] = useState([]);

  useEffect(()=> {

    const styleSelectorLinks = [];

    _.forEach(data.links, (link) => {
      const isSSOLink = _find(link.dataAttributes, {key:'type', value:'sso'})
      if (!isSSOLink) {
        styleSelectorLinks.push(link);
      } 
    })
    
    setCtas(styleSelectorLinks)

  }, [] );

  useEffect(() => {
    if (hasParallaxImages) {
      setTimeout(() => {
        curosrParallaxRef.current = parallax({
          rootElement: rootElRef.current,
          selector: '[data-parallax-target]',
          trigger: 'mouse-move'
        });
        curosrParallaxRef.current.init();
      }, 1000);
    
      return () => {
        curosrParallaxRef.current?.destroy();
      }
    }
  }, []);

  const parallaxImage = useCallback((image, props) => (
    !image.base ? 
    null :
    <div 
    key={props.key}
    className={`${block}__image ${props.gridClasses} parallax-active`}
    style={{ top: props.top }}
    data-parallax-target={true} 
    data-parallax-speed={props.parallaxSpeed}
    data-parallax-constraint={props.parallaxContraint}
    >
      <Picture base={image.base} />
    </div>
  ), []);

  const onClick = {
    'start-style-selector': () =>{ state.setActiveView(VIEWS.choices.name)},
    'navigate-to-personal': () =>{ onClickActions.navigateToPersonal() },
    ...onClickActions
  };

  function renderLink(link) {
    const onClickAction = _.find(link.dataAttributes, {key:'onClickAction'})
    let onClickFunction = _.noop();

    if (onClickAction?.value) {
      onClickFunction = onClick[onClickAction.value]
    }

    return <Button key={link.label} {...link} cssAnimation={true} onClickCallback={onClickFunction} />
  };
  
  return (
    <div className={classNames({
      [`${block}__screen-container`]: true,
      [`${block}__screen-container--${state.activeView}`]: true,
      'utils__section-padding--uniform': true,
      'dark-theme': true
    })}>
      <CssAnimation 
      enter={{ name: 'fade-in', duration: 2 }} 
      triggerEnter={true} 
      renderView={!!data.backgroundVideoUrl}
      >
        <div>
          <BackgroundMedia 
          mediaType={MEDIA_TYPES.video} 
          mediaUrl={data.backgroundVideoUrl} 
          videoConfig={{ muted: state.audioMuted }} 
          overlay={true}
          />
        </div>
      </CssAnimation>
      <CssAnimation 
      enter={{ name: 'short-slide-up-fade-in', duration: 1 }} 
      triggerEnter={true} 
      renderView={hasParallaxImages}
      >
        <div className={classNames({
          [`${block}__images-container`]: true,
          [`${block}__images-container--${state.activeView}`]: true,
          'container-xxxl': true
        })}>
          { 
            data.images?.map((image, index) => parallaxImage(image, parallaxImageProps[index]))
          }
        </div>
      </CssAnimation>
      <div className={classNames({
        [`${block}__content-container`]: true,
        [`${block}__content-container--${state.activeView}`]: true,
        'container-xxxl': true
      })}>
        <AnimatedTextHeader 
        heading={data.title}
        subheading={data.subtitle}
        copy={data.copy}
        headingClasses='headline-var-1'
        subheadingExpandedWidth={true}
        />
        {
          ctas.map(link => (
            renderLink(link)
          ))
        }
      </div>
    </div>
  )
};

LandingScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  rootElRef: propTypes.object.isRequired,
  data: propTypes.object.isRequired
};